import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from './firebase'; 
import { collection, onSnapshot, addDoc, serverTimestamp, query, orderBy, doc, getDoc, updateDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  FaRocket, FaTimes, FaUsers, FaChartBar, FaCheckCircle, 
  FaFilter, FaMobileAlt, FaCloudUploadAlt, 
  FaPaperPlane, FaExclamationTriangle, FaTrash, 
  FaImage, FaVideo, FaFilePdf, FaChartPie, FaCheckDouble, FaCheck, FaEye, FaBan, FaPhone, FaReply, FaCopy, FaExternalLinkAlt
} from 'react-icons/fa';

// --- PRO THEME ---
const THEME = {
  primary: '#2563eb', 
  success: '#10b981', 
  warning: '#f59e0b', 
  error: '#ef4444',    
  bg: '#f1f5f9',      
  surface: '#ffffff', 
  textMain: '#0f172a',
  textSub: '#64748b',
  border: '#e2e8f0'
};

// --- ADVANCED REAL-TIME TRACKING ROW (NOW WITH X-RAY ERRORS) ---
const TrackingRow = ({ item, user, onStatusChange }) => {
  const [realStatus, setRealStatus] = useState(item.status);
  const [liveReason, setLiveReason] = useState(item.reason); // 🔥 NEW: X-Ray Error Catcher

  useEffect(() => {
    if (item.msgId && item.status !== 'failed') {
      const unsub = onSnapshot(doc(db, "businesses", user.uid, "chats", item.phone, "messages", item.msgId), (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const newStatus = data.status || "sent";
          
          if (newStatus !== realStatus) {
            setRealStatus(newStatus);
            onStatusChange(item.phone, newStatus); 
          }
          // Pull exact Webhook error from Meta
          if (data.error) {
             setLiveReason(data.error);
          }
        }
      });
      return () => unsub();
    }
  }, [item, user, realStatus, onStatusChange]);

  const getStatusBadge = () => {
    if (realStatus === 'read') return <span style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: '#ffffff', padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '800', display:'inline-flex', alignItems:'center', gap:'6px', boxShadow: '0 4px 10px rgba(37, 99, 235, 0.3)' }}><FaCheckDouble size={14} /> Read</span>;
    if (realStatus === 'delivered') return <span style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#ffffff', padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '800', display:'inline-flex', alignItems:'center', gap:'6px', boxShadow: '0 4px 10px rgba(16, 185, 129, 0.3)' }}><FaCheckDouble size={14} /> Delivered</span>;
    if (realStatus === 'sent') return <span style={{ background: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)', color: '#ffffff', padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '800', display:'inline-flex', alignItems:'center', gap:'6px', boxShadow: '0 4px 10px rgba(100, 116, 139, 0.3)' }}><FaCheck size={12} /> Sent</span>;
    if (realStatus === 'failed') return <span style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: '#ffffff', padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '800', display:'inline-flex', alignItems:'center', gap:'6px', boxShadow: '0 4px 10px rgba(239, 68, 68, 0.3)' }}><FaBan size={12} /> Failed</span>;
    return <span>{realStatus}</span>;
  };

  return (
    <tr style={{ borderBottom: '1px solid #e2e8f0', transition: 'all 0.2s', ':hover': {background: '#f8fafc'} }}>
      <td style={{ padding: '16px 20px', fontSize: '14px', fontWeight: '800', color: '#0f172a', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>+{item.phone}</td>
      <td style={{ padding: '16px 20px', whiteSpace: 'nowrap' }}>{getStatusBadge()}</td>
      <td style={{ padding: '16px 20px', fontSize: '13px', color: realStatus === 'failed' ? '#b91c1c' : '#10b981', fontWeight: realStatus === 'failed' ? '700' : '600' }}>
          {realStatus === 'failed' ? <span style={{display:'flex', alignItems:'center', gap:'5px'}}><FaExclamationTriangle size={14} style={{flexShrink: 0}}/> {liveReason || item.reason || "Rejected by Meta API Setup Limits"}</span> : <span style={{display:'flex', alignItems:'center', gap:'5px'}}><FaCheckCircle size={14} style={{flexShrink: 0}}/> Processed securely</span>}
      </td>
    </tr>
  );
};

// --- ADVANCED PRO TRACKING MODAL CONTENT ---
const TrackingModalContent = ({ campaign, user, onClose, isMobile }) => {
  const [liveData, setLiveData] = useState(campaign.tracking || []);
  const [filter, setFilter] = useState('all'); 

  const handleStatusChange = useCallback((phone, newStatus) => {
    setLiveData(prev => prev.map(item => item.phone === phone ? { ...item, status: newStatus } : item));
  }, []);

  const total = liveData.length;
  const sentCount = liveData.filter(i => i.status === 'sent').length;
  const deliveredCount = liveData.filter(i => i.status === 'delivered').length;
  const readCount = liveData.filter(i => i.status === 'read').length;
  const failedCount = liveData.filter(i => i.status === 'failed').length;

  const successRate = total > 0 ? Math.round(((deliveredCount + readCount + sentCount) / total) * 100) : 0;
  const readRate = (deliveredCount + readCount) > 0 ? Math.round((readCount / (deliveredCount + readCount)) * 100) : 0;

  const filteredData = liveData.filter(item => filter === 'all' || item.status === filter);

  const StatBox = ({ title, value, icon, gradient, type }) => {
    const isActive = filter === type;
    return (
      <div 
        onClick={() => setFilter(type)}
        style={{ 
          flex: '1 1 calc(20% - 12px)', minWidth: '110px',
          background: isActive ? gradient : 'white', 
          color: isActive ? 'white' : THEME.textMain,
          padding: '16px 12px', borderRadius: '16px', 
          border: isActive ? 'none' : `1px solid ${THEME.border}`, 
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
          boxShadow: isActive ? '0 10px 20px -5px rgba(0,0,0,0.2)' : '0 2px 4px rgba(0,0,0,0.02)', 
          cursor: 'pointer', transition: 'all 0.2s ease',
          transform: isActive ? 'translateY(-2px)' : 'none'
        }}
      >
        <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: isActive ? 'rgba(255,255,255,0.2)' : THEME.bg, color: isActive ? 'white' : THEME.textSub, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
          {icon}
        </div>
        <div style={{ fontSize: '24px', fontWeight: '900', lineHeight: '1' }}>{value}</div>
        <div style={{ fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', marginTop: '6px', letterSpacing: '0.5px', color: isActive ? 'rgba(255,255,255,0.9)' : THEME.textSub, textAlign: 'center' }}>{title}</div>
      </div>
    );
  };

  return (
    <div style={{ background: '#f8fafc', width: '100%', maxWidth: '1050px', height: isMobile ? '100vh' : '88vh', borderRadius: isMobile ? '0' : '24px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.6)' }}>
      
      {/* Header - Fixed Height */}
      <div style={{ padding: isMobile ? '20px' : '25px 35px', borderBottom: `1px solid ${THEME.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', flexShrink: 0 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? '20px' : '26px', fontWeight: '900', color: THEME.textMain, display: 'flex', alignItems: 'center', gap: '10px', letterSpacing: '-0.5px' }}>
            <span style={{background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)', color:'white', width: isMobile ? '32px' : '40px', height: isMobile ? '32px' : '40px', borderRadius:'10px', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 4px 10px rgba(99, 102, 241, 0.3)'}}><FaChartPie size={isMobile ? 14 : 18} /></span> 
            Live Analytics
          </h2>
          <p style={{ margin: '5px 0 0 0', color: THEME.textSub, fontSize: '13px', fontWeight: '500' }}>Tracking: <strong style={{color: THEME.primary}}>{campaign.name}</strong></p>
        </div>
        <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: THEME.textSub }}><FaTimes size={18} /></button>
      </div>

      {/* Progress Bars - Fixed Height */}
      <div style={{ background: 'white', padding: isMobile ? '15px 20px' : '20px 35px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px', borderBottom: `1px solid ${THEME.border}`, flexShrink: 0 }}>
          <div style={{flex: 1}}>
              <div style={{display:'flex', justifyContent:'space-between', marginBottom:'6px', fontSize:'12px', fontWeight:'800', color:THEME.textMain}}>
                  <span>Delivery Success Rate</span>
                  <span style={{color: THEME.success}}>{successRate}%</span>
              </div>
              <div style={{height:'8px', background:'#e2e8f0', borderRadius:'4px', overflow:'hidden'}}>
                  <div style={{height:'100%', width:`${successRate}%`, background:'linear-gradient(90deg, #34d399 0%, #059669 100%)', borderRadius:'4px', transition:'width 1s ease'}}></div>
              </div>
          </div>
          <div style={{flex: 1}}>
              <div style={{display:'flex', justifyContent:'space-between', marginBottom:'6px', fontSize:'12px', fontWeight:'800', color:THEME.textMain}}>
                  <span>Open & Read Rate</span>
                  <span style={{color: THEME.primary}}>{readRate}%</span>
              </div>
              <div style={{height:'8px', background:'#e2e8f0', borderRadius:'4px', overflow:'hidden'}}>
                  <div style={{height:'100%', width:`${readRate}%`, background:'linear-gradient(90deg, #60a5fa 0%, #2563eb 100%)', borderRadius:'4px', transition:'width 1s ease'}}></div>
              </div>
          </div>
      </div>

      {/* Stats Board - Fixed Height */}
      <div style={{ padding: isMobile ? '15px' : '20px 35px', background: '#f8fafc', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <StatBox type="all" title="Audience" value={total} icon={<FaUsers size={16}/>} gradient="linear-gradient(135deg, #475569 0%, #1e293b 100%)" />
          <StatBox type="sent" title="Just Sent" value={sentCount} icon={<FaCheck size={14}/>} gradient="linear-gradient(135deg, #94a3b8 0%, #64748b 100%)" />
          <StatBox type="delivered" title="Delivered" value={deliveredCount} icon={<FaCheckDouble size={14}/>} gradient="linear-gradient(135deg, #34d399 0%, #059669 100%)" />
          <StatBox type="read" title="Read" value={readCount} icon={<FaEye size={16}/>} gradient="linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)" />
          <StatBox type="failed" title="Failed" value={failedCount} icon={<FaBan size={14}/>} gradient="linear-gradient(135deg, #f87171 0%, #dc2626 100%)" />
        </div>
      </div>
      
      {/* Detail Table Section - Allows scrolling */}
      <div style={{ flex: 1, minHeight: 0, padding: isMobile ? '0 15px 15px' : '0 35px 35px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ background: 'white', borderRadius: '16px', border: `1px solid ${THEME.border}`, flex: 1, overflowY: 'auto', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
            <thead style={{ background: '#f1f5f9', borderBottom: `2px solid ${THEME.border}`, position: 'sticky', top: 0, zIndex: 5 }}>
                <tr>
                <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '900', color: THEME.textMain, textTransform: 'uppercase' }}>Phone Number</th>
                <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '900', color: THEME.textMain, textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '900', color: THEME.textMain, textTransform: 'uppercase' }}>Diagnostics</th>
                </tr>
            </thead>
            <tbody>
                {filteredData && filteredData.length > 0 ? (
                filteredData.map((item, idx) => (
                    <TrackingRow key={idx} item={item} user={user} onStatusChange={handleStatusChange} />
                ))
                ) : (
                <tr>
                    <td colSpan="3" style={{ padding: '60px', textAlign: 'center', color: THEME.textSub, fontSize: '14px', fontWeight: '600' }}>
                    <FaChartBar size={40} style={{opacity: 0.1, marginBottom: '15px', display: 'block', margin: '0 auto'}}/>
                    No contacts match the '{filter}' filter.
                    </td>
                </tr>
                )}
            </tbody>
            </table>
        </div>
      </div>
    </div>
  );
};


function Broadcast() {
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showModal, setShowModal] = useState(false);
  const [reportCampaign, setReportCampaign] = useState(null); 
  
  // Data
  const [broadcasts, setBroadcasts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [spaCredentials, setSpaCredentials] = useState({ phoneId: "", token: "", type: "None" });
  
  // Campaign State
  const [campaignName, setCampaignName] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [previewTemplate, setPreviewTemplate] = useState(null); 
  const [broadcastMediaFile, setBroadcastMediaFile] = useState(null); 
  
  // Audience State
  const [inputMethod, setInputMethod] = useState("manual"); 
  const [rawInput, setRawInput] = useState(""); 
  const [validNumbers, setValidNumbers] = useState([]); 
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [needsPrefixCount, setNeedsPrefixCount] = useState(0);

  // Execution
  const [isSending, setIsSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusLog, setStatusLog] = useState("");
  const [failedList, setFailedList] = useState([]);
  const [filterDate, setFilterDate] = useState(""); 

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- 1. DATA LOADER ---
  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u) {
        onSnapshot(query(collection(db, "businesses", u.uid, "templates")), (snap) => {
          setTemplates(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        onSnapshot(query(collection(db, "businesses", u.uid, "broadcasts"), orderBy("createdAt", "desc")), (snap) => {
          setBroadcasts(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        try {
          let token = "", phoneId = "";
          const mainSnap = await getDoc(doc(db, "businesses", u.uid));
          if (mainSnap.exists() && mainSnap.data().accessToken) {
             token = mainSnap.data().accessToken;
             phoneId = mainSnap.data().phoneId;
          } else {
             const settingsSnap = await getDoc(doc(db, "businesses", u.uid, "settings", "whatsapp"));
             if (settingsSnap.exists() && settingsSnap.data().accessToken) {
                token = settingsSnap.data().accessToken;
                phoneId = settingsSnap.data().phoneNumberId || settingsSnap.data().phoneId;
             }
          }
          if (token && phoneId) setSpaCredentials({ token, phoneId, type: "Connected" });
        } catch (error) { console.error(error); }
      }
    });
    return () => unsubscribeAuth();
  }, []);

  // --- 2. TEMPLATE HANDLER ---
  useEffect(() => {
    if (selectedTemplate && templates.length > 0) {
      const tpl = templates.find(t => t.id === selectedTemplate);
      if (tpl) {
        setPreviewTemplate(tpl);
        setBroadcastMediaFile(null); 
      }
    } else {
      setPreviewTemplate(null);
      setBroadcastMediaFile(null);
    }
  }, [selectedTemplate, templates]);

  // --- 3. AUDIENCE ENGINE ---
  const processNumbers = (text) => {
    setRawInput(text);
    if (!text.trim()) { 
      setValidNumbers([]); setDuplicateCount(0); setNeedsPrefixCount(0); return; 
    }
    const rawArray = text.split(/[\n,;\s]+/).map(n => n.replace(/\D/g, '')).filter(n => n.length >= 10);
    const unique = [...new Set(rawArray)];
    
    setValidNumbers(unique);
    setDuplicateCount(rawArray.length - unique.length);
    setNeedsPrefixCount(unique.filter(n => n.length === 10).length);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => processNumbers(event.target.result);
    reader.readAsText(file);
  };

  const applyMagicPrefix = () => {
    const fixed = validNumbers.map(n => n.length === 10 ? '91' + n : n);
    setValidNumbers(fixed);
    setRawInput(fixed.join('\n'));
    setNeedsPrefixCount(0);
  };

  const removeDuplicates = () => {
    setRawInput(validNumbers.join('\n'));
    setDuplicateCount(0);
  };

  // --- 4. BULLETPROOF BROADCAST LOGIC WITH SMART MEDIA DETECTION ---
  const handleLaunch = async (e) => {
    e.preventDefault();
    if (!user || validNumbers.length === 0) return;
    
    if (!spaCredentials.phoneId || !spaCredentials.token) {
        return alert("Meta API Not Connected! Please go to Setup and link your WhatsApp.");
    }

    setIsSending(true);
    setProgress(0);
    setFailedList([]);
    setStatusLog("Checking Connection...");
    const tpl = previewTemplate;

    // 🔥 SMART MEDIA DETECTION 🔥
    let finalMediaUrl = tpl.mediaUrl || ""; // Automatically use the pre-saved template image!

    if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(tpl.headerType)) {
        if (broadcastMediaFile) {
            // User explicitly chose to OVERRIDE the default image
            setStatusLog(`Uploading new override ${tpl.headerType}...`);
            try {
                const storage = getStorage();
                const ext = broadcastMediaFile.name.split('.').pop();
                const storageRef = ref(storage, `broadcasts/${user.uid}/${Date.now()}.${ext}`);
                await uploadBytes(storageRef, broadcastMediaFile);
                finalMediaUrl = await getDownloadURL(storageRef);
            } catch (error) {
                alert("Failed to upload override media. Ensure Firebase Storage is enabled.");
                setIsSending(false); return;
            }
        } else if (!finalMediaUrl) {
            // No saved image and no override image. Error.
            alert(`⚠️ This template requires a ${tpl.headerType} attachment. Please upload one.`);
            setIsSending(false); return;
        }
    }

    const broadcastRef = await addDoc(collection(db, "businesses", user.uid, "broadcasts"), {
      name: campaignName,
      templateName: tpl.name,
      mediaUrl: finalMediaUrl, 
      totalContacts: validNumbers.length,
      sent: 0, 
      failedCount: 0,
      tracking: [],
      status: "Processing", 
      createdAt: serverTimestamp()
    });

    let success = 0;
    let failed = [];
    let trackingData = [];

    for (let i = 0; i < validNumbers.length; i++) {
      let num = validNumbers[i];
      let cleanPhone = num.replace(/\D/g, '');
      if (cleanPhone.length === 10) cleanPhone = '91' + cleanPhone; 
      
      setStatusLog(`Sending to ${cleanPhone}...`);

      const components = [];
      if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(tpl.headerType) && finalMediaUrl) {
          components.push({ type: "header", parameters: [{ type: tpl.headerType.toLowerCase(), [tpl.headerType.toLowerCase()]: { link: finalMediaUrl } }] });
      }

      const varMatches = tpl.body ? tpl.body.match(/{{(\d+)}}/g) : null;
      if (varMatches && varMatches.length > 0) {
         components.push({ type: "body", parameters: varMatches.map(() => ({ type: "text", text: "Customer" })) });
      }

      const payload = {
        messaging_product: "whatsapp",
        recipient_type: "individual",
        to: cleanPhone,
        type: "template",
        template: { 
          name: tpl.name, 
          language: { code: tpl.language || "en_US" }
        }
      };
      if (components.length > 0) payload.template.components = components;

      try {
        const res = await fetch(`https://graph.facebook.com/v24.0/${spaCredentials.phoneId}/messages`, {
          method: "POST",
          headers: { "Authorization": `Bearer ${spaCredentials.token}`, "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
        
        const data = await res.json();

        if (data.error) {
          failed.push({ num: cleanPhone, reason: data.error.message });
          trackingData.push({ phone: cleanPhone, msgId: null, status: "failed", reason: data.error.message });
        } else if (data.messages && data.messages[0].id) {
          success++;
          const msgId = data.messages[0].id;
          trackingData.push({ phone: cleanPhone, msgId: msgId, status: "sent", reason: "" });

          await setDoc(doc(db, "businesses", user.uid, "chats", cleanPhone, "messages", msgId), {
            text: `[Broadcast] ${tpl.name}`, sender: "bot", status: "sent", timestamp: serverTimestamp()
          });
          await setDoc(doc(db, "businesses", user.uid, "contacts", cleanPhone), {
            phone: cleanPhone, lastMessage: `[Broadcast] ${tpl.name}`, lastActive: serverTimestamp()
          }, { merge: true });
        }
      } catch (err) {
        failed.push({ num: cleanPhone, reason: "Network Timeout" });
        trackingData.push({ phone: cleanPhone, msgId: null, status: "failed", reason: "Network Timeout" });
      }

      setProgress(Math.round(((i + 1) / validNumbers.length) * 100));
      await new Promise(r => setTimeout(r, 100)); 
    }

    await updateDoc(doc(db, "businesses", user.uid, "broadcasts", broadcastRef.id), { 
      sent: success, 
      failedCount: failed.length,
      tracking: trackingData, 
      status: "Completed" 
    });

    setFailedList(failed);
    setStatusLog(failed.length > 0 ? "Completed with Meta errors." : "All Sent Successfully!");
    if(success > 0 && failed.length === 0) setTimeout(() => { setShowModal(false); setIsSending(false); }, 1500);
    else setIsSending(false); 
  };

  const handleDeleteBroadcast = async (id) => {
    if (window.confirm("Are you sure you want to delete this campaign history?")) {
        try { await deleteDoc(doc(db, "businesses", user.uid, "broadcasts", id)); } 
        catch (error) { alert("Failed to delete."); }
    }
  };

  const filteredBroadcasts = broadcasts.filter(camp => {
    if (!filterDate) return true;
    const campDate = camp.createdAt?.toDate ? camp.createdAt.toDate().toISOString().split('T')[0] : '';
    return campDate === filterDate;
  });

  const StatCard = ({ label, value, icon, color }) => (
    <div style={{ background: 'white', borderRadius: '16px', padding: '20px', flex: '1 1 auto', minWidth: '150px', boxShadow: '0 2px 4px rgba(0,0,0,0.04)', border: `1px solid ${THEME.border}`, display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '12px', fontWeight: '700', color: THEME.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
        <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: `${color}15`, color: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
      </div>
      <div style={{ fontSize: '28px', fontWeight: '800', color: THEME.textMain }}>{value}</div>
    </div>
  );

  if (!user) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading System...</div>;

  return (
    <div style={{ fontFamily: '"Inter", sans-serif', background: THEME.bg, minHeight: '100vh', padding: isMobile ? '15px' : '30px' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '15px' : '0', marginBottom: '25px' }}>
        <div>
          <h1 style={{ fontSize: isMobile ? '24px' : '28px', fontWeight: '900', color: THEME.textMain, margin: 0 }}>Campaigns</h1>
          <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
             {spaCredentials.phoneId ? 
                <span style={{ fontSize: '12px', fontWeight: '700', color: THEME.success, background: `${THEME.success}15`, padding: '4px 10px', borderRadius: '6px' }}>● API Linked</span> : 
                <span style={{ fontSize: '12px', fontWeight: '700', color: THEME.error, background: `${THEME.error}15`, padding: '4px 10px', borderRadius: '6px' }}>● API Missing</span>
             }
          </div>
        </div>
        <button onClick={() => { setCampaignName(""); setValidNumbers([]); setRawInput(""); setBroadcastMediaFile(null); setShowModal(true); }} style={{ width: isMobile ? '100%' : 'auto', justifyContent: 'center', background: THEME.textMain, color: 'white', border: 'none', padding: '14px 28px', borderRadius: '12px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', transition: 'transform 0.2s' }}>
          <FaRocket /> New Campaign
        </button>
      </div>

      {/* STATS */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '15px', marginBottom: '25px', flexWrap: 'wrap' }}>
        <StatCard label="Total Sent" value={broadcasts.reduce((a, b) => a + (b.sent || 0), 0).toLocaleString()} icon={<FaPaperPlane />} color={THEME.primary} />
        <StatCard label="Campaigns" value={broadcasts.length} icon={<FaChartBar />} color={THEME.warning} />
        <StatCard label="Audience Reach" value={broadcasts.reduce((a, b) => a + (b.totalContacts || 0), 0).toLocaleString()} icon={<FaUsers />} color={THEME.success} />
      </div>

      {/* TABLE */}
      <div style={{ background: 'white', borderRadius: '16px', border: `1px solid ${THEME.border}`, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
        <div style={{ padding: '20px', borderBottom: `1px solid ${THEME.border}`, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '15px' : '0', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: THEME.textMain }}>Broadcast History</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
             <span style={{ fontSize: '12px', fontWeight: '700', color: THEME.textSub }}><FaFilter /> Filter Date:</span>
             <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: `1px solid ${THEME.border}`, fontSize: '13px', outline: 'none', background: THEME.bg, flex: isMobile ? 1 : 'none' }} />
             {filterDate && <button onClick={() => setFilterDate("")} style={{ border: 'none', background: THEME.errorLight, color: THEME.error, padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>Clear</button>}
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead style={{ background: '#f8fafc', borderBottom: `1px solid ${THEME.border}` }}>
              <tr>
                {['Campaign', 'Status', 'Audience', 'Sent', 'Failed', 'Date', 'Action'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '15px 20px', fontSize: '12px', fontWeight: '800', color: THEME.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredBroadcasts.map(camp => (
                <tr key={camp.id} style={{ borderBottom: `1px solid ${THEME.border}` }}>
                  <td style={{ padding: '16px 20px', fontWeight: '800', color: THEME.textMain, fontSize: '14px' }}>{camp.name}</td>
                  <td style={{ padding: '16px 20px' }}>
                    <span style={{ fontSize: '11px', fontWeight: '800', padding: '6px 12px', borderRadius: '20px', whiteSpace: 'nowrap', background: camp.status === 'Completed' ? `${THEME.success}20` : camp.status === 'Scheduled' ? `${THEME.warning}20` : `${THEME.primary}20`, color: camp.status === 'Completed' ? THEME.success : camp.status === 'Scheduled' ? THEME.warning : THEME.primary }}>
                      {camp.status}
                    </span>
                  </td>
                  <td style={{ padding: '16px 20px', color: THEME.textSub, fontWeight: '600' }}>{camp.totalContacts}</td>
                  <td style={{ padding: '16px 20px', fontWeight: '800', color: THEME.success }}>{camp.sent}</td>
                  <td style={{ padding: '16px 20px', fontWeight: '800', color: camp.failedCount > 0 ? THEME.error : THEME.textSub }}>{camp.failedCount || 0}</td>
                  <td style={{ padding: '16px 20px', color: THEME.textSub, fontSize: '13px', fontWeight: '600' }}>{camp.createdAt?.toDate ? camp.createdAt.toDate().toLocaleDateString() : '-'}</td>
                  <td style={{ padding: '16px 20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button onClick={() => setReportCampaign(camp)} style={{ border: 'none', background: '#eff6ff', color: THEME.primary, padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s' }}>
                        <FaChartPie /> Track
                    </button>
                    <button onClick={() => handleDeleteBroadcast(camp.id)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#cbd5e1', padding: '5px' }} title="Delete Campaign"><FaTrash size={16}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- REAL-TIME TRACKING MODAL --- */}
      {reportCampaign && (
         <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '0' : '20px' }}>
            <TrackingModalContent campaign={reportCampaign} user={user} onClose={() => setReportCampaign(null)} isMobile={isMobile} />
         </div>
      )}

      {/* --- WIZARD MODAL (SENDING) --- */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(5px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '0px' : '30px' }}>
          <div style={{ background: 'white', width: '100%', maxWidth: '1200px', height: isMobile ? '100vh' : '90vh', borderRadius: isMobile ? '0px' : '24px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', overflow: isMobile ? 'auto' : 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)' }}>
            
            {/* LEFT: CONFIGURATION */}
            <div style={{ flex: 1.5, padding: isMobile ? '20px' : '40px', overflowY: isMobile ? 'visible' : 'auto', borderRight: isMobile ? 'none' : `1px solid ${THEME.border}`, background: '#ffffff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '900', color: THEME.textMain }}>New Broadcast</h2>
                  <p style={{ margin: 0, color: THEME.textSub, fontSize: '14px', fontWeight: '500' }}>Configure your message and audience.</p>
                </div>
                <button onClick={() => setShowModal(false)} style={{ border: 'none', background: THEME.bg, width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: THEME.textSub }}><FaTimes size={18} /></button>
              </div>

              {isSending && (
                <div style={{ marginBottom: '30px', padding: '20px', background: '#eff6ff', borderRadius: '16px', border: '1px solid #bfdbfe' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontWeight: '700', color: '#1e40af', fontSize: '14px' }}>
                    <span>{statusLog}</span>
                    <span>{progress}%</span>
                  </div>
                  <div style={{ width: '100%', height: '12px', background: '#dbeafe', borderRadius: '6px', overflow: 'hidden' }}>
                    <div style={{ width: `${progress}%`, height: '100%', background: 'linear-gradient(90deg, #60a5fa 0%, #2563eb 100%)', transition: 'width 0.2s ease' }}></div>
                  </div>
                </div>
              )}

              {failedList.length > 0 && (
                <div style={{ marginBottom: '30px', padding: '20px', background: '#fef2f2', borderRadius: '16px', border: '1px solid #fecaca', maxHeight: '180px', overflowY: 'auto' }}>
                  <div style={{ fontWeight: '800', color: '#991b1b', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}><FaExclamationTriangle /> Meta Errors Log</div>
                  <div style={{ fontSize: '12px', color: '#b91c1c', marginBottom: '12px', fontWeight: '600' }}>*If it says "Parameter mismatch", check your template variables.*</div>
                  {failedList.map((f, i) => (
                    <div key={i} style={{ fontSize: '13px', color: '#b91c1c', borderBottom: '1px solid #fee2e2', padding: '6px 0', fontWeight: '500' }}>
                      <strong>{f.num}</strong>: {f.reason}
                    </div>
                  ))}
                </div>
              )}

              <form onSubmit={handleLaunch} style={{ display: isSending ? 'none' : 'block' }}>
                <div style={{ marginBottom: '25px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', color: THEME.textSub, marginBottom: '10px' }}>Campaign Name</label>
                  <input required autoFocus placeholder="Summer Sale Blast" value={campaignName} onChange={e => setCampaignName(e.target.value)} style={{ width: '100%', padding: '16px', borderRadius: '14px', border: `1px solid ${THEME.border}`, fontSize: '15px', outline: 'none', background: THEME.bg, fontWeight: '600', boxSizing: 'border-box' }} />
                </div>

                <div style={{ marginBottom: '25px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', color: THEME.textSub, marginBottom: '10px' }}>Message Template</label>
                  <select required value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)} style={{ width: '100%', padding: '16px', borderRadius: '14px', border: `1px solid ${THEME.border}`, fontSize: '15px', outline: 'none', background: 'white', fontWeight: '600', boxSizing: 'border-box' }}>
                    <option value="">-- Select a Template --</option>
                    {templates.filter(t => t.status === "Approved").map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>

                {/* 🔥 FIX: SMART MEDIA OVERRIDE DETECTION 🔥 */}
                {previewTemplate && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(previewTemplate.headerType) && (
                  <div style={{ marginBottom: '25px', padding: '20px', background: previewTemplate.mediaUrl ? '#f8fafc' : '#eff6ff', borderRadius: '16px', border: `2px dashed ${previewTemplate.mediaUrl ? THEME.border : '#bfdbfe'}` }}>
                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', color: THEME.primary, marginBottom: '10px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {previewTemplate.headerType === 'IMAGE' ? <FaImage size={16}/> : previewTemplate.headerType === 'VIDEO' ? <FaVideo size={16}/> : <FaFilePdf size={16}/>} 
                        {previewTemplate.mediaUrl ? 'Attached Template Media' : `Upload Broadcast ${previewTemplate.headerType}`}
                      </span>
                    </label>
                    
                    {previewTemplate.mediaUrl && !broadcastMediaFile && (
                        <div style={{marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', color: THEME.success, fontWeight: '700', fontSize: '12px'}}>
                            <FaCheckCircle /> Using pre-approved template media
                        </div>
                    )}

                    <p style={{fontSize: '12px', color: THEME.textSub, marginBottom: '12px', fontWeight: '500', lineHeight: '1.4'}}>
                      {previewTemplate.mediaUrl 
                        ? "You can optionally upload a new file below to override the default media for this specific broadcast." 
                        : "Because you selected a media template, you must upload the file to send."}
                    </p>
                    <input type="file" accept={previewTemplate.headerType === 'IMAGE' ? 'image/*' : previewTemplate.headerType === 'VIDEO' ? 'video/*' : '.pdf'} onChange={e => setBroadcastMediaFile(e.target.files[0])} style={{fontSize: '13px', width: '100%', padding: '8px', background: 'white', borderRadius: '8px', border: `1px solid ${THEME.border}`}} />
                  </div>
                )}

                <div style={{ marginBottom: '30px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', color: THEME.textSub, marginBottom: '10px' }}>Target Audience</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
                    <button type="button" onClick={() => setInputMethod('manual')} style={{ padding: '10px 18px', borderRadius: '12px', fontSize: '13px', fontWeight: '700', border: inputMethod === 'manual' ? `2px solid ${THEME.primary}` : `1px solid ${THEME.border}`, background: inputMethod === 'manual' ? '#eff6ff' : 'white', color: inputMethod === 'manual' ? THEME.primary : THEME.textSub, cursor: 'pointer', transition: 'all 0.2s' }}>Manual Paste</button>
                    <label style={{ padding: '10px 18px', borderRadius: '12px', fontSize: '13px', fontWeight: '700', border: `1px solid ${THEME.border}`, background: 'white', color: THEME.textSub, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
                      <FaCloudUploadAlt size={16} /> Upload CSV <input type="file" hidden accept=".csv,.txt" onChange={handleFileUpload} />
                    </label>
                  </div>

                  {inputMethod === 'manual' && (
                    <textarea placeholder="Paste mobile numbers here..." value={rawInput} onChange={e => processNumbers(e.target.value)} style={{ width: '100%', height: '140px', padding: '16px', borderRadius: '14px', border: `1px solid ${THEME.border}`, fontSize: '14px', fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box', background: THEME.bg }} />
                  )}

                  {(duplicateCount > 0 || needsPrefixCount > 0) && (
                    <div style={{ marginTop: '15px', padding: '15px 20px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '12px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '15px' : '0', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '8px' : '20px', fontSize: '13px', fontWeight: '700', color: '#92400e' }}>
                        {duplicateCount > 0 && <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}><FaExclamationTriangle/> {duplicateCount} Duplicates</span>}
                        {needsPrefixCount > 0 && <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}><FaExclamationTriangle/> {needsPrefixCount} Missing +91</span>}
                      </div>
                      <div style={{ display: 'flex', gap: '10px', width: isMobile ? '100%' : 'auto' }}>
                        {duplicateCount > 0 && <button type="button" onClick={removeDuplicates} style={{ flex: isMobile ? 1 : 'none', padding: '8px 14px', background: 'white', border: '1px solid #fcd34d', borderRadius: '8px', fontSize: '12px', fontWeight: '800', cursor: 'pointer', color: '#92400e' }}>Fix Duplicates</button>}
                        {needsPrefixCount > 0 && <button type="button" onClick={applyMagicPrefix} style={{ flex: isMobile ? 1 : 'none', padding: '8px 14px', background: '#f59e0b', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '800', cursor: 'pointer', color: 'white' }}>Auto +91</button>}
                      </div>
                    </div>
                  )}

                  {validNumbers.length > 0 && (
                    <div style={{ marginTop: '15px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '800', color: THEME.success, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}><FaCheckCircle/> {validNumbers.length} Valid Contacts Ready</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxHeight: '120px', overflowY: 'auto', padding: '12px', background: '#f8fafc', borderRadius: '14px', border: `1px solid ${THEME.border}` }}>
                        {validNumbers.slice(0, 100).map((n, i) => (<span key={i} style={{ background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '4px 10px', fontSize: '12px', fontWeight: '600', color: '#334155' }}>{n}</span>))}
                        {validNumbers.length > 100 && <span style={{ fontSize: '12px', color: '#64748b', padding: '4px 10px', fontWeight: '600' }}>+{validNumbers.length - 100} more...</span>}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '15px' }}>
                  <button type="button" onClick={() => setShowModal(false)} style={{ flex: 1, padding: '18px', background: '#f1f5f9', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '15px', color: '#64748b', cursor: 'pointer' }}>Cancel</button>
                  <button type="submit" disabled={validNumbers.length === 0} style={{ flex: 2, padding: '18px', background: THEME.primary, border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '16px', color: 'white', cursor: validNumbers.length > 0 ? 'pointer' : 'not-allowed', boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                    <FaRocket /> Blast Campaign
                  </button>
                </div>
              </form>
            </div>

            {/* RIGHT: LIVE DEVICE PREVIEW */}
            <div style={{ flex: 1, background: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: isMobile ? 'flex-start' : 'center', borderLeft: isMobile ? 'none' : `1px solid ${THEME.border}`, padding: isMobile ? '30px 0' : '0', position: 'relative', minHeight: isMobile ? 'auto' : '100%', overflowY: isMobile ? 'visible' : 'auto' }}>
              {!isMobile && <div style={{ position: 'absolute', top: '30px', fontWeight: '900', color: '#94a3b8', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1.5px' }}>Device Preview</div>}
              
              <div style={{ transform: isMobile ? 'scale(0.9)' : 'none', transformOrigin: 'top center', display: 'flex', justifyContent: 'center', width: '100%' }}>
                <div style={{ width: '340px', minHeight: '660px', background: '#121212', borderRadius: '50px', padding: '14px', boxShadow: '0 30px 60px -15px rgba(0,0,0,0.4)', border: '2px solid #334155' }}>
                  <div style={{ width: '100%', height: '100%', background: '#efeae2', borderRadius: '38px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    
                    {/* Phone Header */}
                    <div style={{ height: '60px', background: '#008069', display: 'flex', alignItems: 'center', padding: '0 20px', gap: '12px' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#008069', fontWeight: '900' }}>Logo</div>
                      <div>
                          <div style={{ color: 'white', fontSize: '16px', fontWeight: '700', lineHeight: '1.2' }}>Tesla CRM</div>
                          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>bot</div>
                      </div>
                    </div>
                    
                    {/* Chat Window */}
                    <div style={{ flex: 1, padding: '20px 15px', backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")', backgroundSize: 'cover', overflowY: 'auto' }}>
                      {previewTemplate ? (
                        <div style={{ maxWidth: '92%' }}>
                            <div style={{ background: 'white', borderRadius: '0 16px 16px 16px', padding: '5px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            {['IMAGE', 'VIDEO'].includes(previewTemplate.headerType) && (
                                <div style={{ height: '160px', background: '#e2e8f0', borderRadius: '12px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', color: '#94a3b8' }}>
                                    {broadcastMediaFile ? <img src={URL.createObjectURL(broadcastMediaFile)} alt="override preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/> : previewTemplate.mediaUrl ? <img src={previewTemplate.mediaUrl} alt="template preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/> : <FaMobileAlt size={40} />}
                                </div>
                            )}
                            {previewTemplate.headerType === 'TEXT' && <div style={{fontWeight:'800', fontSize:'16px', padding:'8px 8px 0', color: '#111b21'}}>{previewTemplate.headerText}</div>}
                            <div style={{ padding: '8px', fontSize: '15px', lineHeight: '1.5', color: '#111b21', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{previewTemplate.body.replace('{{1}}', 'Customer')}</div>
                            {previewTemplate.footer && <div style={{ padding: '0 8px', fontSize: '12px', color: '#667781', marginBottom: '6px' }}>{previewTemplate.footer}</div>}
                            <div style={{ textAlign: 'right', fontSize: '11px', color: '#667781', paddingRight: '6px', paddingBottom: '4px' }}>{new Date().toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</div>
                            </div>

                            {/* Buttons */}
                            {previewTemplate.buttons && previewTemplate.buttons.length > 0 && (
                                <div style={{ marginTop: '8px', width: '100%' }}>
                                {previewTemplate.buttons.map((btn, i) => (
                                    <div key={i} style={{ background: 'white', borderRadius: '12px', padding: '12px', marginTop: '6px', textAlign: 'center', color: '#00a884', fontWeight: '700', fontSize: '15px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                    {btn.type === 'PHONE_NUMBER' && <FaPhone size={14}/>}
                                    {btn.type === 'URL' && <FaExternalLinkAlt size={14}/>}
                                    {btn.type === 'QUICK_REPLY' && <FaReply size={14}/>}
                                    {btn.type === 'COPY_CODE' && <FaCopy size={14}/>}
                                    {btn.type === 'COPY_CODE' ? "Copy Offer Code" : btn.text || "Action Button"}
                                    </div>
                                ))}
                                </div>
                            )}
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', color: '#8696a0', marginTop: '60%', fontSize: '14px', background: 'rgba(255,255,255,0.9)', padding: '12px', borderRadius: '12px', fontWeight: '600' }}>Select a template to view rendering</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

export default Broadcast;